import { useCallback, useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Slider } from "./Slider";
import { type SliderDataProps, sliderDataSchema } from "./Slider.schema";
import { SliderControl } from "./SliderControl";
import { SliderIndicator } from "./SliderIndicator";
import { SliderLabel } from "./SliderLabel";
import { SliderThumb } from "./SliderThumb";
import { SliderTrack } from "./SliderTrack";
import { SliderValue } from "./SliderValue";
import { SLIDER_TAG_NAME } from "./constants";

export interface SliderHandle {
  value: (value: number) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [SLIDER_TAG_NAME]: { props: SliderDataProps; handle: SliderHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-slider": HTMLElement & SliderHandle;
  }
}

export function RWCSlider({
  ref,
  disabled: initialDisabled,
  value: initialValue,
  min,
  label,
  tooltip,
  ...props
}: SliderDataProps & { ref?: Ref<SliderHandle> }) {
  const host = useIslandHost();

  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [value, setValue] = useState(initialValue ?? min ?? 0);

  const handle: SliderHandle = {
    value: setValue,
    disabled: (value = true) => setDisabled(value),
  };

  useImperativeHandle(ref, () => handle, []);

  const syncFormValue = useCallback(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : String(value));
  }, [host, disabled, value]);

  useEffect(syncFormValue, [syncFormValue]);

  return (
    <Slider
      {...props}
      min={min}
      value={value}
      disabled={disabled}
      onValueChange={setValue}
    >
      {label ? <SliderLabel>{label}</SliderLabel> : null}
      <SliderValue />
      <SliderControl>
        <SliderTrack>
          <SliderIndicator />
          <SliderThumb tooltip={tooltip} />
        </SliderTrack>
      </SliderControl>
    </Slider>
  );
}

componentManager.register(
  rwc(SLIDER_TAG_NAME, {
    component: RWCSlider,
    dataSchema: sliderDataSchema,
    formAssociated: true,
  }),
);
